import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useResizeObserver from 'use-resize-observer/polyfilled';
import { mergeRefs } from '#/shared/utils/react';
import * as Styled from './styled';

/**
 * As of early 2023 html `input` elements can't natively adapt their width to
 * their contents in the way, e.g., a `div` can.  This component copies the
 * contents of the input in a hidden `span`, calculates its width then applies
 * that to the input
 */
const DynamicWidthInput = forwardRef(
  ({ value, placeholder, className, ...restInputProps }, ref) => {
    const inputRef = useRef();
    const [inputValue, setInputValue] = useState();

    useEffect(() => {
      // getting the input value from the DOM Element automatically
      // incorporates the `defaultValue` if defined
      setInputValue(inputRef.current?.value);
    }, [value]);

    const [width, setWidth] = useState();
    const { ref: proxyRef, width: proxyWidth } = useResizeObserver();

    useEffect(() => {
      setWidth(proxyWidth);
    }, [proxyWidth]);

    // ensures that `0` is treated as a truthy value
    const coalesceSpanValue = (values, fallback = '') =>
      values.find(v => ![undefined, null, ''].includes(v)) ?? fallback;

    return (
      <>
        <Styled.Input
          value={value}
          placeholder={placeholder}
          width={width}
          className={className}
          ref={mergeRefs(ref, inputRef)}
          // all standard `input` attribute props need to be forwarded to the
          // native input element, passing them individually is too onerous
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...restInputProps}
        />
        <Styled.WidthProxySpan ref={proxyRef} data-proxy-element>
          {coalesceSpanValue([inputValue, placeholder])}
        </Styled.WidthProxySpan>
      </>
    );
  }
);

DynamicWidthInput.displayName = 'DynamicWidthInput';

DynamicWidthInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
};

DynamicWidthInput.defaultProps = {
  value: undefined,
  placeholder: undefined,
  className: undefined,
};

export default DynamicWidthInput;
